import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { ToastService } from './toast.service';
import { API_ROUTES } from '../constants/api-routes';
import {
  RegisterRequest,
  LoginRequest,
  VerifyEmailRequest,
  ResendVerificationRequest,
  SocialMediaLoginRequest,
  CreateFamilySpaceAndMemberRequest,
  AuthResponseModel,
  SocialProviderLoginType,
} from '../models/auth.models';
import { ApiResponse } from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private dataService = inject(DataService);
  private toastService = inject(ToastService);

  private verificationPendingSubject = new BehaviorSubject<boolean>(false);
  verificationPending$: Observable<boolean> = this.verificationPendingSubject.asObservable();

  isLoggedIn(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return !!localStorage.getItem('accessToken');
  }

  isVerificationPending(): boolean {
    return this.verificationPendingSubject.value;
  }

  /**
   * Register a new user with email and password
   */
  async register(data: RegisterRequest): Promise<ApiResponse<string>> {
    const resp = await this.apiService.request<string>(
      'POST',
      API_ROUTES.REGISTER,
      data,
      { useToken: false, showToaster: false }
    );

    if (resp.status) {
      this.verificationPendingSubject.next(true);
      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.setItem('pendingVerificationEmail', data.email);
      }
    }

    return resp;
  }

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<ApiResponse<AuthResponseModel>> {
    const resp = await this.apiService.request<AuthResponseModel>(
      'POST',
      API_ROUTES.LOGIN,
      data,
      { useToken: false, showToaster: false }
    );

    if (resp.status && resp.data) {
      const authData = resp.data;

      // Check if email verification is pending
      if (authData.isVerificationPending) {
        this.verificationPendingSubject.next(true);
        if (isPlatformBrowser(this.platformId)) {
          sessionStorage.setItem('pendingVerificationEmail', authData.email);
        }
        this.toastService.warning(
          'Email verification pending. Please verify your email to continue.'
        );
        return resp;
      }

      // Save token and profile
      this.dataService.setProfile({
        userId: authData.userId,
        email: authData.email,
        firstName: authData.firstName,
        lastName: authData.lastName,
        fullName: authData.fullName || `${authData.firstName} ${authData.lastName}`.trim(),
        token: authData.accessToken,
        profileImagePath: '',
      });

      this.verificationPendingSubject.next(false);
    }

    return resp;
  }

  /**
   * Verify email using token from verification link
   */
  async verifyEmail(token: string): Promise<ApiResponse<string>> {
    const data: VerifyEmailRequest = { token };
    const resp = await this.apiService.request<string>(
      'POST',
      API_ROUTES.VERIFY_EMAIL,
      data,
      { useToken: false, showToaster: false }
    );

    if (resp.status) {
      this.verificationPendingSubject.next(false);
      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.removeItem('pendingVerificationEmail');
      }
    }

    return resp;
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<ApiResponse<string>> {
    const data: ResendVerificationRequest = { email };
    return this.apiService.request<string>(
      'POST',
      API_ROUTES.RESEND_VERIFICATION,
      data,
      { useToken: false, showToaster: false }
    );
  }

  /**
   * Social media login (Google, Facebook, Apple)
   */
  async socialMediaLogin(data: SocialMediaLoginRequest): Promise<ApiResponse<AuthResponseModel>> {
    const resp = await this.apiService.request<AuthResponseModel>(
      'POST',
      API_ROUTES.SOCIAL_LOGIN,
      data,
      { useToken: false, showToaster: false }
    );

    if (resp.status && resp.data) {
      const authData = resp.data;

      // Save token and profile
      this.dataService.setProfile({
        userId: authData.userId,
        email: authData.email,
        firstName: authData.firstName,
        lastName: authData.lastName,
        fullName: authData.fullName || `${authData.firstName} ${authData.lastName}`.trim(),
        token: authData.accessToken,
        profileImagePath: '',
      });

      this.verificationPendingSubject.next(false);
    }

    return resp;
  }

  /**
   * Forgot password: send reset link to email
   */
  async forgotPassword(email: string): Promise<ApiResponse<string>> {
    return this.apiService.request<string>(
      'POST',
      API_ROUTES.FORGOT_PASSWORD,
      { email },
      { useToken: false, showToaster: false }
    );
  }

  /**
   * Reset password with token and new password
   */
  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<string>> {
    return this.apiService.request<string>(
      'POST',
      API_ROUTES.RESET_PASSWORD,
      { token, newPassword },
      { useToken: false, showToaster: false }
    );
  }

  /**
   * Accept family space invitation — validates tokens from invite URL
   */
  async acceptInvitation(token: string, familySpaceToken: string): Promise<ApiResponse<string>> {
    return this.apiService.request<string>(
      'POST',
      API_ROUTES.ACCEPT_INVITATION,
      { token, familySpaceToken },
      { useToken: false, showToaster: false, showLoader: false, extraHeaders: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } }
    );
  }

  /**
   * Validate family member email before adding to list
   */
  async validateFamilyMemberEmail(email: string): Promise<ApiResponse<boolean>> {
    return this.apiService.request<boolean>(
      'POST',
      API_ROUTES.VALIDATE_FAMILY_MEMBER_EMAIL,
      { email: email.trim() },
      { showToaster: false }
    );
  }

  /**
   * Create family space and family members in one API call
   */
  async createFamilySpaceAndMember(
    payload: CreateFamilySpaceAndMemberRequest
  ): Promise<ApiResponse<string>> {
    return this.apiService.request<string>(
      'POST',
      API_ROUTES.CREATE_FAMILY_SPACE_AND_MEMBER,
      payload,
      { showToaster: false }
    );
  }

  /**
   * Skip the create-family-space step
   */
  async skipFamilySpace(): Promise<ApiResponse<null>> {
    return this.apiService.request<null>(
      'POST',
      API_ROUTES.SKIP_FAMILY_SPACE,
      { isSkip: true },
      { showToaster: false }
    );
  }

  /**
   * Logout: clear all stored auth data
   */
  logout(): void {
    this.clearStorage();
    this.dataService.clearProfile();
    this.verificationPendingSubject.next(false);
    this.router.navigate(['/login']);
  }

  /**
   * Logout without redirect (for use during cleanup)
   */
  logoutWithoutRedirect(): void {
    this.clearStorage();
    this.dataService.clearProfile();
    this.verificationPendingSubject.next(false);
  }

  private clearStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userProfile');
      sessionStorage.removeItem('pendingVerificationEmail');
    }
  }
}
